import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, PlayCircle, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";

const when = (s) => new Date(s).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const AskBox = ({ call, onSent }) => {
  const [q, setQ] = useState("");
  const send = async () => {
    try { await api.post(`/calls/${call.id}/questions`, { question: q }); setQ(""); toast.success("Question sent to Lindsay"); onSent(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 flex flex-col sm:flex-row gap-2">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="What would you like Lindsay to cover?" data-testid={`call-question-input-${call.id}`} className="flex-1 h-10 rounded-lg border border-input bg-white px-3 text-sm" />
      <button onClick={send} disabled={q.trim().length < 3} data-testid={`call-question-send-${call.id}`} className="rounded-full bg-navy text-white px-5 h-10 text-sm disabled:opacity-50">Send question</button>
    </div>
  );
};

export default function Calls() {
  const [d, setD] = useState(null);
  const load = () => api.get("/calls").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const now = new Date().toISOString();
  const upcoming = d.calls.filter((c) => c.starts_at >= now).reverse();
  const past = d.calls.filter((c) => c.starts_at < now);
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="The Roots Club Pro" title="Live group calls with Lindsay." />
      {!d.is_pro && (
        <div className="rounded-2xl bg-navy text-white p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="calls-upgrade">
          <div className="flex items-start gap-3"><Lock className="h-5 w-5 text-sun mt-1" /><p className="text-sm max-w-md">Fortnightly live calls, question submission and recordings are part of Pro (£79/month).</p></div>
          <Link to="/app/membership" className="rounded-full bg-pinkb px-5 py-2 text-sm" data-testid="calls-upgrade-btn">See Pro</Link>
        </div>
      )}
      <h2 className="font-serif uppercase text-xl text-navy mb-3">Coming up</h2>
      {upcoming.length === 0 && <p className="text-sm text-muted-foreground mb-6" data-testid="calls-none">The next call date will appear here soon.</p>}
      {upcoming.map((c) => (
        <div key={c.id} className="rounded-2xl border-2 border-pinkb/30 bg-card p-6 mb-4" data-testid={`call-${c.id}`}>
          <div className="text-xs font-mono uppercase text-pinkb">{when(c.starts_at)}</div>
          <div className="font-serif uppercase text-2xl text-navy mt-1">{c.title}</div>
          {c.notes && <p className="text-sm text-muted-foreground mt-1">{c.notes}</p>}
          {c.join_url && <a href={c.join_url} target="_blank" rel="noreferrer" onClick={() => api.post(`/calls/${c.id}/joined`).catch(() => {})} data-testid={`call-join-${c.id}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-pinkb text-white px-5 py-2 text-sm"><Video className="h-4 w-4" />Join the call</a>}
          {d.is_pro && <AskBox call={c} onSent={load} />}
          {c.my_questions.length > 0 && <ul className="mt-3 text-sm space-y-1">{c.my_questions.map((q) => <li key={q.id} className="rounded bg-secondary px-3 py-1.5">{q.question}{q.answered && <span className="ml-2 text-xs text-leaf">Answered</span>}</li>)}</ul>}
        </div>
      ))}
      <h2 className="font-serif uppercase text-xl text-navy mt-10 mb-3">Recordings</h2>
      {past.length === 0 && <p className="text-sm text-muted-foreground">No recordings yet.</p>}
      <div className="space-y-2">{past.map((c) => (
        <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid={`recording-${c.id}`}>
          <span><span className="font-medium">{c.title}</span> · {when(c.starts_at)}</span>
          {c.recording_url ? <a href={c.recording_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-pinkb"><PlayCircle className="h-4 w-4" />Watch</a> : <span className="text-muted-foreground text-xs">{d.is_pro ? "Recording coming soon" : "Pro only"}</span>}
        </div>
      ))}</div>
    </div>
  );
}
